import {Injectable} from '@angular/core';
import {Offer} from '../../../../models/offer/offer.model';
import {ApplicationStateService} from '../../../../../shared/services/application-state.service';
import {BehaviorSubject} from 'rxjs/BehaviorSubject';
import {OfferService} from '../../offer.service';
import {Subject, Subscription} from 'rxjs/Rx';
import {MatDialog, MatDialogRef} from '@angular/material';
import {MetricsDataModel} from '../../../../../shared/components/models/metrics/metrics-data.model';
import {CustomMetricsName} from '../../../../../shared/components/models/metrics/custom-metrics-name.enum';
import {MetricsType} from '../../../../../shared/components/models/metrics/metrics-type.enum';
import {MetricsService} from '../../../../../shared/services/metrics.service';
import {NavigateManagerService} from '../../../navigate-manager.service';
import {IframeCommunicationService} from '../../../../../shared/services/iframe-communication.service';
import {isNotNullOrUndefined} from 'codelyzer/util/isNotNullOrUndefined';
import {PrepareRegistrationService} from '../../prepare-registration.service';
import {RegistrationService} from '../../registration.service';
import {SortOrder} from '../../../../../shared/enums/sort-order.enum';
import {FiltersService} from '../../../../../shared/services/filters.service';
import {OffersPresentationMode} from '../../offers-presentation.service';
import {
  OffersView
} from '../../../../../shared/sections/web/next-offers-section/offers-control-panel/offers-control-panel.component';
import {
  OffersComparisonDialogComponent
} from '../../../../../shared/components/dialogs/offers-comparison-dialog/offers-comparison-dialog.component';
import {isNext} from '../../../../utils/utils';
import {
  OffersPresentationComponent
} from '../../../../../shared/sections/web/offer-section/offers-presentation/offers-presentation.component';
import {InsuranceCompanyService} from '../../../../../shared/services/insurance-company.service';
import {
  OfferUpdateDialogComponent
} from '../../../../../shared/components/dialogs/offer-update-dialog/offer-update-dialog.component';
import {OfferResponseStatusCode} from '../../../../enums/offer/offer-response-status-code.enum';
import {
  OfferUnavailableDialogComponent
} from '../../../../../shared/components/dialogs/offer-unavailable-dialog/offer-unavailable-dialog.component';
import {TestingPresetsService} from '../../../../../shared/services/debugging/testing-presets.service';
import {TestingPresetKey} from '../../../../enums/debugging/testing-preset-key.enum';
import {DeviceService} from '../../../../../shared/services/device.service';

@Injectable({
  providedIn: 'root'
})
export class NextOffersSectionStateService {

  offersUpdate$: BehaviorSubject<void> = new BehaviorSubject(null);
  offersViewChange$: BehaviorSubject<OffersView> = new BehaviorSubject(null);
  offers: Offer[]; // filter and/or sorted raw offers
  offersComparisonPanelVisible: boolean = false;
  isNext: boolean = isNext();

  private initFilters: Map<string, object>;
  private offersChange: Subscription;
  private comparisonState: OffersComparisonState;
  // metrics
  private comparisonMetric: MetricsDataModel;
  private selectMetric: MetricsDataModel;
  private offerDetailsMetric: MetricsDataModel;
  private activeOffersView: OffersView;
  private offerUpdateDialogRef: MatDialogRef<OfferUpdateDialogComponent>;
  private readonly GET_OFFER_TIMEOUT = 15;

  get selectedOffer(): Offer {
    return this.stateService.selectedOffer;
  }

  get comparisonOffersAmount(): number {
    return this.comparisonState && this.comparisonState.offers ? this.comparisonState.offers.length : 0;
  }

  get comparisonOffers(): Offer[] {
    return this.comparisonState ? this.comparisonState.offers : [];
  }

  get addOfferToComparisonAllowed(): boolean {
    return this.comparisonOffersAmount < this.comparisonState.maxOffersAmount;
  }

  get rawOffers(): Offer[] {
    return this.offerService.offers ? this.offerService.offers : [];
  }

  get isListView(): boolean {
    return this.activeOffersView === OffersView.LIST;
  }

  get isTableView(): boolean {
    return this.activeOffersView === OffersView.TABLE;
  }

  get wsConnected(): boolean {
    return this.offerService.wsConnected;
  }

  get selectedOfferStatusCode(): OfferResponseStatusCode {
    return this.stateService.state.selectedOfferStatusCode;
  }

  get isGetOfferRequired(): boolean {
    return this.insuranceCompanyService.isGetOfferRequired(this.stateService.insuranceCompanyCode);
  }

  get shouldReconnectWebsocketForGetOffer(): boolean {
    return this.isGetOfferRequired && this.selectedOfferStatusCode === OfferResponseStatusCode.CALCULATE_OFFER && !this.wsConnected;
  }

  get shouldRejectGetOffer(): boolean {
    return TestingPresetsService.isPresetActive(TestingPresetKey.REJECT_GET_OFFER);
  }

  constructor(private stateService: ApplicationStateService,
              private dialog: MatDialog,
              private registrationService: RegistrationService,
              private navigateManager: NavigateManagerService,
              private metricsService: MetricsService,
              private filtersService: FiltersService,
              private prepareRegistrationService: PrepareRegistrationService,
              private iframeCommunicationService: IframeCommunicationService,
              private offerService: OfferService,
              private insuranceCompanyService: InsuranceCompanyService) {
    this.init();
  }

  updateOffers(): void {
    const filteredOffers = this.filtersService.filterOffers(this.offerService.offers);
    this.filtersService.updateOffersCountState(filteredOffers);
    this.offers = this.sortOffersByPolicyAmount(filteredOffers, SortOrder.ASCENDING);
    this.offersUpdate$.next();
  }

  selectOffer(offer: Offer): void {
    const missingPersonalDataParams = this.offerService.getMissingPersonalDataParams(offer.id);
    if (missingPersonalDataParams.length || this.offerService.isEmailAddressMissingAndRequired()) { // read only - open dialog
      this.navigateManager.openMissingPersonalDataDialog(missingPersonalDataParams);
    } else { // no missing params - navigate to payment step
      if (isNotNullOrUndefined(offer)) {
        this.changeOffer(offer);
      }
      this.sendSelectMetric(offer);
      this.iframeCommunicationService.onStepChange();
    }
  }

  isOfferInComparison(offer: Offer): boolean {
    return !!this.comparisonState.offers.find(comparisonOffer => comparisonOffer.id === offer.id);
  }

  addOfferToComparison(offer: Offer): void {
    if (this.addOfferToComparisonAllowed) {
      this.comparisonState.offers.push(offer);
      this.sortComparisonOffers();
    }
    this.updateComparisonPanelVisibility();
  }

  removeOfferFromComparison(offer: Offer): void {
    this.comparisonState.offers = this.comparisonState.offers.filter(comparisonOffer => comparisonOffer.id !== offer.id);
    this.updateComparisonPanelVisibility();
  }

  clearOffersComparison(): void {
    this.comparisonState.offers = [];
    this.updateComparisonPanelVisibility();
  }

  showOffersComparison(): void {
    const selectOfferEvent = new Subject<Offer>();
    selectOfferEvent.subscribe(offer => this.selectOffer(offer));
    this.openComparisonMetric();
    const dialogComponent: any = this.isNext ? OffersComparisonDialogComponent : OffersPresentationComponent;
    this.dialog.open(dialogComponent,
        dialogComponent.getConfigBuilder().buildConfiguration({
          offers: this.comparisonState.offers,
          mode: OffersPresentationMode.OFFERS_COMPARISON,
          selectOfferEvent,
        })).afterClosed().subscribe(offer => {
      if (offer) {
        this.selectOffer(offer);
      }
      this.closeComparisonMetric();
    });
  }

  showOfferDetails(offer: Offer, fromOffersStep: boolean = true): void {
    this.openDetailsMetric(offer);
    const dialogComponent: any = this.isNext ? OffersComparisonDialogComponent : OffersPresentationComponent;
    this.dialog.open(dialogComponent,
        dialogComponent.getConfigBuilder().buildConfiguration({
          offers: [offer],
          mode: OffersPresentationMode.OFFER_DETAILS,
          selectOfferEvent: null,
          fromOffersStep: fromOffersStep
        })).afterClosed().subscribe(selectedOffer => {
      this.closeDetailsMetric();
      if (selectedOffer) {
        this.selectOffer(selectedOffer);
      }
    });
  }

  sortOffersByPolicyAmount(offers: Offer[], sortOrder: SortOrder): Offer[] {
    switch (sortOrder) {
      case SortOrder.ASCENDING:
        return offers.sort((a: Offer, b: Offer) => Offer.getOfferLowestPrice(a) - Offer.getOfferLowestPrice(b));
      case SortOrder.DESCENDING:
        return offers.sort((a: Offer, b: Offer) => Offer.getOfferLowestPrice(b) - Offer.getOfferLowestPrice(a));
      default:
        return offers;
    }
  }

  getOfferById(id: string): Offer {
    return this.offerService.offers.find(offer => offer.id === id);
  }

  changeView(view: OffersView): void {
    this.activeOffersView = view;
    this.offersViewChange$.next(view);
    this.updateComparisonPanelVisibility();
  }

  reconnectWebsocketForGetOffer(): void {
    this.offerService.reconnectOffersWebsocket();
    this.handleGetOffer();
  }

  private init(): void {
    this.offersChange = this.offerService.$offersChange.subscribe(() => this.updateOffers());
    this.filtersService.filtersChangeEvent.subscribe(() => this.updateOffers());
    this.initComparisonState();
  }

  private initComparisonState(): void {
    this.comparisonState = {
      maxOffersAmount: document.body.clientWidth < 750 ? 2 : 4,
      offers: []
    };
  }

   private changeOffer(offer: Offer, isGetOffer?: boolean) {
    this.stateService.saveNecessaryBusinessParamsFromOffer(offer.id);
    this.stateService.selectedOffer = offer;
    this.stateService.selectedOfferStatusCode = isGetOffer ? OfferResponseStatusCode.GET_OFFER : OfferResponseStatusCode.CALCULATE_OFFER;
    this.sendOfferMetrics(offer);
    this.registrationService.resetOfferRegistrationData(false, isGetOffer);
    if (this.registrationService.hasRegistrationState && !this.registrationService.dataFromSessionIncluded) { // include data from session only after first offer select
      this.registrationService.includeDataFromSession();
    }
    this.stateService.selectedOfferOriginalModifications = (offer && !!offer.modifications) ? offer.modifications : null;
    if (offer) {
      if (!isGetOffer) {
        this.prepareRegistrationService.sendPrepareRegistration(offer.id);
      }
      if (this.insuranceCompanyService.isGetOfferRequired(this.stateService.insuranceCompanyCode)) {
        if (!isGetOffer) {
          this.handleGetOffer();
          this.navigateManager.goNext(false);
        }
      } else {
        this.navigateManager.goNext();
      }
      this.iframeCommunicationService.onStepChange();
    }
  }

  private handleGetOffer(): void {
    this.openOfferUpdateDialog();

    const waitingForGetOfferTimeout = setTimeout(() => {
      const afterClosed$ = this.offerUpdateDialogRef.afterClosed().subscribe(() => {
        afterClosed$.unsubscribe();
        if (this.selectedOfferStatusCode === OfferResponseStatusCode.CALCULATE_OFFER) {
          this.openOfferUnavailableDialog();
        }
      });
      this.offerUpdateDialogRef.close();
      eventStreamSubscription.unsubscribe();
    }, this.GET_OFFER_TIMEOUT * 1000);

    let eventStreamSubscription = this.offerService.getOfferEvent.subscribe(
        (getOfferEvent: Offer) => {
          if (isNotNullOrUndefined(getOfferEvent) && !this.shouldRejectGetOffer) {
            clearTimeout(waitingForGetOfferTimeout);
            this.offerUpdateDialogRef.close();
            this.changeOffer(getOfferEvent, true);
            eventStreamSubscription.unsubscribe();
          }
        }
    );
  }

  private openComparisonMetric(): void {
    this.comparisonMetric = new MetricsDataModel(CustomMetricsName.OFFERS_COMPARISON, MetricsType.COMPONENT);
    this.comparisonMetric.open();
    this.comparisonMetric.value = JSON.stringify({
      offersIds: this.comparisonState.offers.map(offer => offer.id),
      calculationId: this.offerService.calculationId,
      processId: this.offerService.processId
    });
  }

  private closeComparisonMetric(): void {
    this.comparisonMetric.close();
    this.metricsService.send(this.comparisonMetric);
  }

  private openDetailsMetric(offer: Offer): void {
    this.offerDetailsMetric = new MetricsDataModel(CustomMetricsName.OFFER_DETAILS, MetricsType.COMPONENT);
    this.offerDetailsMetric.open();
    this.offerDetailsMetric.value = JSON.stringify({
      offerId: offer.id,
      calculationId: this.offerService.calculationId,
      processId: this.offerService.processId
    });
  }

  private closeDetailsMetric(): void {
    this.offerDetailsMetric.close();
    this.metricsService.send(this.offerDetailsMetric);
  }

  private sendSelectMetric(offer: Offer): void {
    this.selectMetric = new MetricsDataModel(CustomMetricsName.SELECT_OFFER, MetricsType.BUTTON);
    this.selectMetric.change(JSON.stringify({
      offerId: offer.id,
      calculationId: this.offerService.calculationId,
      processId: this.offerService.processId
    }));
    this.metricsService.send(this.selectMetric);
  }

  private sendOfferMetrics(offer: Offer): void {
    if (offer.modifications && offer.modifications.length > 0) {
      this.metricsService.sendSimpleMetric(CustomMetricsName.INITIAL_MODIFICATIONS, MetricsType.PROCESS, JSON.stringify(offer.modifications));
    }
  }

  private updateComparisonPanelVisibility(): void {
    this.offersComparisonPanelVisible = this.comparisonOffersAmount > 0 && this.isListView;
  }

  private sortComparisonOffers(): void {
    if (isNotNullOrUndefined(this.comparisonState)) {
      this.comparisonState.offers = this.sortOffersByPolicyAmount(this.comparisonState.offers, SortOrder.ASCENDING);
    }
  }

  private openOfferUpdateDialog(): void {
    setTimeout(() => {
      this.offerUpdateDialogRef = this.dialog.open(OfferUpdateDialogComponent, OfferUpdateDialogComponent.getConfigBuilder().buildConfiguration({
        canCloseOnNavigation: false
      }));
    }, 300);
  }

  private openOfferUnavailableDialog(): void {
    this.dialog.open(OfferUnavailableDialogComponent, OfferUnavailableDialogComponent.getConfigBuilder().buildConfiguration({
      canCloseOnNavigation: false
    }));
  }
}

export interface OffersComparisonState {
  maxOffersAmount: number; // defines max amount of offers that can be add to comparison
  offers: Offer[];
}
